import {
  FlexRowCenterHorizontal,
  Icon,
  LoadingSpinner,
  useBreakpoint,
  useRouter,
} from '@main/core-ui';
import type { RequestHistory } from '@main/dsr-types';
import {
  AccessiblePopover,
  FocusButton,
  usePopover,
} from '@main/pc-core-components';
import {
  selectCoreIdentifier,
  selectIsLoggedIn,
} from '@main/privacy-center/src/containers/App/selectors';
import RequestPreview from '@main/privacy-center/src/containers/RequestPreview';
import { useDownloadZip } from '@main/privacy-center/src/hooks';
import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { takeControlMessages } from '../TakeControl/messages';
import { pendingRequestsMessages } from './messages';
import {
  FooterWrapper,
  MobileBellIconWrapper,
  MoreWrapper,
  NotificationCountWrapper,
  RequestsWrapper,
  ShowWrapper,
  StyledA,
  StyledButton,
} from './wrappers';

/**
 * The PendingRequests props
 */
export interface PendingRequestsProps {
  /** Fetch the next page of requests */
  fetchMore?: () => void;
  /** The list of requests to display */
  requests: RequestHistory[];
  /** Message to show as preview to the popover */
  preview: React.ReactNode;
  /** Whether network is loading new requests */
  loading: boolean;
  /** On click */
  onClick?: () => void;
  /** The total number of active requests */
  activeNotificationCount: number;
  /** Whether to show all pending requests */
  showAll: boolean;
  /** Callback for changing showAll */
  setShowAll: (newShowAll: boolean) => void;
}

/**
 * Dropdown that renders the RequestPreview blocks
 */
function PendingRequests({
  requests,
  preview,
  loading,
  fetchMore,
  onClick,
  showAll,
  setShowAll,
  activeNotificationCount,
}: PendingRequestsProps): JSX.Element {
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const { formatMessage } = useIntl();
  const { isBeforeTablet, isAtLeastTablet } = useBreakpoint();
  const popover = usePopover();
  const coreIdentifier = useSelector(selectCoreIdentifier);
  const { openPopover, closePopover } = popover;
  const [downloadZip, status] = useDownloadZip();
  const { queryParams } = useRouter();

  // if a modal is open, close the popover
  useEffect(() => {
    if (queryParams.modal) {
      closePopover();
    }
  }, [queryParams, closePopover]);

  const onButtonClick = onClick || openPopover;

  const content = (
    <section
      id="request-history-controls"
      role="none"
      style={{ padding: '12px' }}
    >
      <RequestsWrapper role="menu">
        {requests.map((request) => (
          <li role="menuitem" key={request.id}>
            <RequestPreview
              {...request}
              downloadZip={downloadZip}
              downloadStatus={status}
            />
          </li>
        ))}
        {requests.length === 0 && !loading && (
          <small>
            {formatMessage(pendingRequestsMessages.noRequests, {
              takeControl: formatMessage(takeControlMessages.takeControl),
              user: coreIdentifier?.value || '-',
            })}
          </small>
        )}
        {fetchMore && (
          <MoreWrapper>
            <StyledA onClick={fetchMore}>
              {formatMessage(pendingRequestsMessages.more)}
            </StyledA>
          </MoreWrapper>
        )}
      </RequestsWrapper>
      {loading && (
        <FlexRowCenterHorizontal>
          <LoadingSpinner />
        </FlexRowCenterHorizontal>
      )}
      {activeNotificationCount > 0 && (
        <FooterWrapper>
          <ShowWrapper onClick={() => setShowAll(!showAll)}>
            {formatMessage(
              showAll
                ? pendingRequestsMessages.showLess
                : pendingRequestsMessages.showMore,
            )}
          </ShowWrapper>
        </FooterWrapper>
      )}
    </section>
  );

  return (
    <div id="dsr-history-popover">
      <AccessiblePopover popover={popover} yPosition="bottom" xPosition="right">
        {content}
      </AccessiblePopover>
      {isBeforeTablet && (
        <FocusButton
          onClick={onButtonClick}
          aria-haspopup="true"
          aria-controls="request-history-controls"
          aria-label={formatMessage(pendingRequestsMessages.mobileLabel, {
            activeNotificationCount,
          })}
        >
          {isLoggedIn ? (
            <NotificationCountWrapper>
              {activeNotificationCount}
            </NotificationCountWrapper>
          ) : (
            <MobileBellIconWrapper>
              <Icon type="notifications" size="2.0em" />
            </MobileBellIconWrapper>
          )}
        </FocusButton>
      )}
      {isAtLeastTablet && (
        <StyledButton
          onClick={onButtonClick}
          id="request-history"
          buttonType="secondary"
          aria-haspopup="true"
          aria-controls="request-history-controls"
        >
          {preview}
        </StyledButton>
      )}
    </div>
  );
}

export default PendingRequests;
